import { Box, Button, Grid, Modal, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { medicalsApi } from "../../api/medicalsApi";
import { QueryKeys } from "../../constants/queryKeys";
import {
  NotificationVariant,
  useNotifications,
} from "../../components/dashboard/NotificationsProvider";
import FindOrCreatePatientInput from "../../components/widgets/modals/encounter/FindOrCreatePatientInput";
import useFullPageLoader from "../../hooks/useFullPageLoader";
import {
  useOrganization,
  useOrganizationReference,
} from "../facility/hooks/useOrganization";
import { Reference } from "../../types/reference";
import { Patient } from "../../types/patient";
import { generateHumanDisplayName } from "../../utils/DataTransformationUtils";

type Props = {
  open: boolean;
  onClose: () => void;
  /** When set (e.g. from patient Actions), skip patient search and create for this patient. */
  initialPatient?: Patient;
};

function patientToSubject(patient: Patient): Reference {
  const name = generateHumanDisplayName(patient.name?.[0]);

  return {
    type: "Patient",
    reference: `Patient/${patient.id}`,
    identifier: patient.id,
    display: name,
  };
}

const AiNoteCreateModal = ({ open, onClose, initialPatient }: Props) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setNotification } = useNotifications();
  const organizationReference = useOrganizationReference();
  const { organization } = useOrganization();
  const [, toggleLoader] = useFullPageLoader();
  const [subject, setSubject] = useState<Reference>();

  useEffect(() => {
    if (open && initialPatient?.id) {
      setSubject(patientToSubject(initialPatient));
    }
  }, [open, initialPatient]);

  const handleClose = () => {
    setSubject(undefined);
    onClose();
  };

  const handleCreate = async () => {
    if (!subject?.identifier) {
      setNotification({
        message: "Please select or register a patient.",
        variant: NotificationVariant.error,
      });
      return;
    }

    try {
      toggleLoader?.(true);
      // Omit note so the scribe UI treats this as a fresh note (Transcript
      // tab, "No note generated", "generating" copy). note: {} is truthy.
      const created = await medicalsApi.createCopilot({
        subject,
        custodian: organizationReference || {
          reference: `Organization/${organization?.id}`,
          identifier: organization?.id,
          type: "Organization",
          display: organization?.name,
        },
        status: "active",
        transcripts: [],
        date: new Date(),
      });

      if (!created?.id) {
        throw new Error("AI note was created but no id was returned.");
      }

      await queryClient.invalidateQueries({ queryKey: [QueryKeys.AI_NOTES] });
      setNotification({
        message: "AI note created. You can start scribing.",
        variant: NotificationVariant.success,
      });
      handleClose();
      navigate(`/ai-notes/${created.id}`);
    } catch (error) {
      console.error("Error creating AI note:", error);
      setNotification({
        message:
          error instanceof Error
            ? error.message
            : "Unable to create AI note. Please try again.",
        variant: NotificationVariant.error,
      });
    } finally {
      toggleLoader?.(false);
    }
  };

  const hasInitialPatient = Boolean(initialPatient?.id);

  return (
    <Modal open={open} onClose={handleClose} disableEnforceFocus>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "95%", sm: 720 },
          bgcolor: "background.paper",
          boxShadow: 24,
          borderRadius: 1,
          p: 3,
        }}
      >
        <Typography variant="h6" mb={1}>
          New AI Note
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          {hasInitialPatient
            ? "Create a standalone AI scribe note for this patient. You can create a medical encounter from this note later if needed."
            : "Select or register a patient to start a standalone AI scribe note. You can create a medical encounter from this note later if needed."}
        </Typography>

        <Grid container spacing={2}>
          <FindOrCreatePatientInput
            subject={subject}
            setSubject={setSubject}
            checkOpenEncounters={false}
            addPatientButtonText="Add patient to AI Note"
            patientCreatedMessage="Successfully registered the patient for this AI note."
          />
        </Grid>

        <Box display="flex" justifyContent="flex-end" gap={1} mt={3}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!subject?.identifier}
            onClick={handleCreate}
          >
            Create AI Note
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AiNoteCreateModal;
